/**
 * Picking one object out of a stack of overlapping ones, in the elevations
 * view.
 *
 * The elevation draws each object as an absolutely-positioned div with NO
 * z-index, so paint order is `room.objects` order and the browser gives every
 * click to whatever happens to be painted last. Anything under it is
 * unreachable — measured across 1500 dev rooms, 56 objects in 52 rooms are
 * ≥90% covered by a later-drawn one (a refrigerator under the surround built
 * around it, a tub support box under its tub, a washer/dryer under its
 * laundry surround), and 160 more are 50-90% covered, leaving only a sliver.
 *
 * The rule here is `GroupIsolationView`'s, which already solved this for the
 * group editor: SMALLEST FIRST (the smallest box under the cursor is the most
 * specific thing there), keep the current selection when it is in the stack,
 * and cycle on a repeat click. Deliberately NOT the floor plan's other answer
 * — a type-priority table (bathroom fixtures over furniture, chairs over
 * tables, cabinets over countertops). Area alone gets the same answers here
 * without a list to keep up to date.
 */

/** The `[data-elevation-object-id]` boxes under a viewport point, smallest first. */
export function hitStackAt(clientX, clientY, doc = document) {
  // The browser already knows the stack, so there is no second copy of the
  // drawing's geometry to keep in step — and it hit-tests what is actually
  // painted, including the SVG schematics.
  const els =
    typeof doc?.elementsFromPoint === 'function'
      ? doc.elementsFromPoint(clientX, clientY)
      : [];
  const byId = new Map();
  for (const el of els) {
    const host = el.closest?.('[data-elevation-object-id]');
    const id = host?.dataset?.elevationObjectId;
    if (!id || byId.has(id)) continue;
    const r = host.getBoundingClientRect();
    byId.set(id, { id, area: r.width * r.height });
  }
  return [...byId.values()].sort((a, b) => a.area - b.area);
}

export const EMPTY_CYCLE = { items: [], index: 0 };

/**
 * Which object a press on the stack refers to.
 *
 * Three cases, in order:
 *  1. the same stack as last time AND the selection is still the one the cycle
 *     points at — the user is clicking again to see what is underneath, so
 *     report a CONTINUATION and let the caller advance on mouse-UP. Advancing
 *     on the press would change what a drag drags, mid-gesture.
 *  2. the selection is somewhere in this stack — keep it, and re-seat the
 *     cycle on it. Without this, pressing an already-selected object to drag
 *     it would jump the selection to the smallest thing under the cursor.
 *  3. otherwise a fresh pick: the smallest box, i.e. the most specific object.
 *
 * @param {string[]} hits      ids, smallest-area first (`hitStackAt` order).
 * @param {string|null} selectedId
 * @param {{items: string[], index: number}} cycle
 */
export function resolveHitSelection({
  hits = [],
  selectedId = null,
  cycle = EMPTY_CYCLE,
}) {
  if (!hits.length) {
    return { id: null, cycle: EMPTY_CYCLE, isContinuation: false, count: 0 };
  }
  const count = hits.length;
  const sameStack =
    cycle.items.length === hits.length &&
    cycle.items.every((id) => hits.includes(id));
  const cyclePick = cycle.items[cycle.index];
  if (sameStack && count > 1 && selectedId && selectedId === cyclePick) {
    return { id: selectedId, cycle, isContinuation: true, count };
  }
  if (selectedId && hits.includes(selectedId)) {
    return {
      id: selectedId,
      cycle: { items: hits, index: hits.indexOf(selectedId) },
      isContinuation: false,
      count,
    };
  }
  return {
    id: hits[0],
    cycle: { items: hits, index: 0 },
    isContinuation: false,
    count,
  };
}

/** The next object round the stack — called on mouse-up, never on the press. */
export function advanceHitCycle(cycle) {
  if (!cycle?.items?.length) return { id: null, cycle: EMPTY_CYCLE };
  const index = (cycle.index + 1) % cycle.items.length;
  return { id: cycle.items[index], cycle: { items: cycle.items, index } };
}
